import React, { createContext, useContext } from 'react';
import { AnalyticsContext } from '@atlaskit/analytics-next';
import { fg } from '@atlaskit/platform-feature-flags';

export type ForgeUIRuntimeType = 'webworker' | 'iframe' | 'iframev2';

// Read via the useForgeUIRuntime hook, not directly.
const ForgeUIRuntimeContext = createContext<ForgeUIRuntimeType | undefined>(undefined);

/** Rendering runtime from the nearest provider, or `undefined` outside one (e.g. gate off). */
export const useForgeUIRuntime = (): ForgeUIRuntimeType | undefined =>
	useContext(ForgeUIRuntimeContext);

/**
 * Contributes the rendering runtime to `forgeUIAttributes` for descendant analytics events, and
 * exposes it via {@link useForgeUIRuntime} for descendant error boundaries.
 */
export const ForgeUIRuntimeAnalyticsContext = ({
	runtime,
	children,
}: {
	children: React.ReactNode;
	runtime: ForgeUIRuntimeType;
}): React.JSX.Element => {
	// Gate the attribute on the bridge-core v2 rollout: only emit `runtime` once
	// `forge-ui-bridge-core-reset` is on, leaving legacy v1 analytics untouched.
	if (!fg('forge-ui-bridge-core-reset')) {
		return <>{children}</>;
	}

	return (
		<ForgeUIRuntimeContext.Provider value={runtime}>
			<AnalyticsContext data={{ forgeUIAttributes: { runtime } }}>{children}</AnalyticsContext>
		</ForgeUIRuntimeContext.Provider>
	);
};
